// AddViewSupportProjectComponent.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Row, Col, Label, Table, Input,
  Modal, ModalHeader, ModalBody, Badge
} from "reactstrap";
import { Formik, Field, Form } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import getYear from "date-fns/getYear";
import getMonth from "date-fns/getMonth";
import { BsPlusCircle } from "react-icons/bs";
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import th from "date-fns/locale/th";
import { th as thBuddhist } from "date-fns/esm/locale";
import { addYears } from "date-fns/esm/fp";
import { format, parseISO } from "date-fns";

// ---------- Thai/Buddhist locale ----------
registerLocale("th-buddhist", {
  ...th,
  options: { ...th.options, weekStartsOn: 1 },
  formatLong: thBuddhist.formatLong,
});

// ---------- helpers ----------
const thaiMonths = ["มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน","กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม"];
const range = (start, end) => new Array(end - start).fill().map((_, i) => i + start);
const years = range(1963, new Date().getFullYear() + 8);

const fmtMoney = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatMonthYearBE(date) {
  if (!date) return "";
  const be = addYears(543, date);
  return format(be, "MMMM yyyy", { locale: th });
}
function toThaiDateLabel(iso) {
  if (!iso) return "-";
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  const be = addYears(543, d);
  return `${format(be, "dd")} ${format(be, "MMMM", { locale: th })} ${format(be, "yyyy")}`;
}
function parseYearMonth(yyyymm) {
  const [y, m] = (yyyymm || "").split("-").map(Number);
  return { y, m };
}
function sameMonth(iso, y, m) {
  const d = typeof iso === "string" ? parseISO(iso) : iso;
  return d.getFullYear() === y && (d.getMonth() + 1) === m;
}

// ===============================================================
//                        MAIN COMPONENT
// ===============================================================
export function AddViewSupport411Component() {
  const [reportMonth, setReportMonth] = useState("");     // "YYYY-MM"
  const [reportMonthDate, setReportMonthDate] = useState(null);
  const user = JSON.parse(localStorage.getItem("userData") || "{}");
  const deptId = user?.deptId || "";

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isEditingReport, setIsEditingReport] = useState(false);

  // master: สาขาอาชีพ (เพื่อแสดงชื่อ ถ้าต้องใช้ให้ต่อเอง—ที่ตารางนี้ไม่ได้ใช้)
  // const [lawOcuData, setLawOcuData] = useState([]);

  // sync string <-> Date
  useEffect(() => {
    if (!reportMonth) { setReportMonthDate(null); return; }
    const [y, m] = reportMonth.split("-").map(Number);
    setReportMonthDate(new Date(y, m - 1, 1));
  }, [reportMonth]);

  // summary
  const summary = useMemo(() => {
    const tutor = items.reduce((a, r) => a + Number(r.tutorAmountExpense || 0), 0);
    const lunch = items.reduce((a, r) => a + Number(r.lunchAmountExpense || 0), 0);
    const util  = items.reduce((a, r) => a + Number(r.utilityAmountExpense || 0), 0);
    const other = items.reduce((a, r) => a + Number(r.otherAmountExpense  || 0), 0);
    const total = tutor + lunch + util + other;
    const refund = items.reduce((a, r) => a + Number(r.balanceTotalAmount || 0), 0);
    return { tutor, lunch, util, other, total, refund };
  }, [items]);

  // Load month
  const checkAndLoadMonth = async () => {
    if (!reportMonth || !deptId) {
      Swal.fire("กรุณาเลือกรอบรายงาน (เดือน/ปี)", "", "warning");
      return;
    }
    try {
      setLoading(true);
      const q = new URLSearchParams({ monthYear: reportMonth, deptId });
      const res = await fetch(`/api/Support/viewProject?${q.toString()}`);
      const j = await res.json();
      if (j?.status !== 1) throw new Error(j?.message || "โหลดข้อมูลไม่ได้");

      const rows = (j?.dataenum || []).map((s, i) => ({
        id: s.supportOperationProjectId ?? i + 1,
        deptId: s.deptId,
        projectName: s.projectName || "-",
        courseName: s.courseName || "-",
        testingPersonAmount: Number(s.testingPersonAmount || 0),
        passPersonAmount: Number(s.passPersonAmount || 0),
        tutorAmountExpense: Number(s.tutorAmountExpense || 0),
        lunchAmountExpense: Number(s.lunchAmountExpense || 0),
        utilityAmountExpense: Number(s.utilityAmountExpense || 0),
        otherName: s.otherName || "",
        otherAmountExpense: Number(s.otherAmountExpense || 0),
        paymentDate: s.paymentDate, // yyyy-MM-dd
        balanceTotalAmount: Number(s.balanceTotalAmount || 0),
      }));

      setItems(rows);
      setIsEditingReport(rows.length > 0);
    } catch (e) {
      console.error(e);
      Swal.fire("โหลดข้อมูลเดือนไม่สำเร็จ", e?.message || "", "error");
      setItems([]);
      setIsEditingReport(false);
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setEditingItem(null); setOpenModal(true); };
  const openEdit = (row) => { setEditingItem(row); setOpenModal(true); };
  const closeModal = () => setOpenModal(false);

  return (
    <Container className="py-3">
      {/* Header controls */}
      <Row className="g-3 align-items-end">
        <Col md={5}>
          <Label>รอบรายงาน (เดือน/ปี)</Label>
          <DatePicker
            selected={reportMonthDate}
            onChange={(date) => {
              if (date instanceof Date && !isNaN(date)) {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                setReportMonth(`${y}-${m}`);
                setReportMonthDate(new Date(y, Number(m) - 1, 1));
              } else {
                setReportMonth(""); setReportMonthDate(null);
              }
            }}
            dateFormat="MMMM yyyy"
            showMonthYearPicker
            showFullMonthYearPicker
            locale="th-buddhist"
            placeholderText="เลือกเดือน/ปี (พ.ศ.)"
            value={formatMonthYearBE(reportMonthDate)}
            renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
              <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                <div className="d-flex align-items-center" style={{ gap: 8 }}>
                  <select className="form-select form-select-sm" style={{ width: 120 }}
                    value={thaiMonths[getMonth(date)]}
                    onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}
                  >
                    {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <select className="form-select form-select-sm" style={{ width: 100 }}
                    value={getYear(date)}
                    onChange={({ target: { value } }) => changeYear(Number(value))}
                  >
                    {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                  </select>
                </div>
                <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
              </div>
            )}
            customInput={
              <input className="form-control rounded-pill text-center input-color" readOnly
                value={formatMonthYearBE(reportMonthDate)} placeholder="เลือกเดือน/ปี (พ.ศ.)" />
            }
          />
        </Col>

        <Col md="auto" className="pt-4">
          <button className="btn btn-primary" onClick={checkAndLoadMonth} disabled={!reportMonth}>
            ตรวจสอบ/โหลดรายงาน
          </button>
        </Col>

        <Col md="auto" className="pt-4">
          <button className="btn btn-success" onClick={openAdd} disabled={!reportMonth}>
            <BsPlusCircle className="me-1" /> เพิ่มรายการ
          </button>
        </Col>

        <Col md="auto" className="pt-4">
          {reportMonth && (
            <Badge color={isEditingReport ? "warning" : "success"} pill className="px-3 py-2">
              {isEditingReport ? "โหมดแก้ไขรายงาน" : "โหมดสร้างรายงานใหม่"}
            </Badge>
          )}
        </Col>
      </Row>

      {/* Table */}
      <div className="mt-3">
        <Table bordered responsive>
          <thead className="table-light">
            <tr>
              <th rowSpan="2" className="text-center align-middle">ที่</th>
              <th rowSpan="2" className="text-center align-middle">โครงการ</th>
              <th rowSpan="2" className="text-center align-middle">หลักสูตร</th>
              <th rowSpan="2" className="text-center align-middle">จำนวนผู้เข้ารับการฝึกอบรมฯ<br/>(คน)</th>
              <th rowSpan="2" className="text-center align-middle">จำนวนผู้ผ่านการฝึกอบรมฯ<br/>(คน)</th>
              <th colSpan="4" className="text-center align-middle">รายการค่าใช้จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan="2" className="text-center align-middle">รวม<br/>(บาท)</th>
              <th rowSpan="2" className="text-center align-middle">วันที่จ่ายเงินช่วยเหลือหรืออุดหนุน</th>
              <th rowSpan="2" className="text-center align-middle">เงินคงเหลือส่งคืน<br/>(บาท)</th>
              <th rowSpan="2" className="text-center align-middle">จัดการ</th>
            </tr>
            <tr>
              <th className="text-center align-middle">ค่าตอบแทนวิทยากร<br/>(บาท)</th>
              <th className="text-center align-middle">ค่าอาหารกลางวัน<br/>(บาท)</th>
              <th className="text-center align-middle">ค่าวัสดุ<br/>(บาท)</th>
              <th className="text-center align-middle">ค่าใช้จ่ายอื่นๆ (ระบุ…)</th>
            </tr>
          </thead>

          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={13} className="text-center">ยังไม่มีรายการ</td></tr>
            )}

            {items.map((r, idx) => {
              const total =
                Number(r.tutorAmountExpense||0) +
                Number(r.lunchAmountExpense||0) +
                Number(r.utilityAmountExpense||0) +
                Number(r.otherAmountExpense||0);
              return (
                <tr key={r.id || idx}>
                  <td className="text-center">{idx + 1}</td>
                  <td>{r.projectName}</td>
                  <td>{r.courseName}</td>
                  <td className="text-center">{Number(r.testingPersonAmount || 0).toLocaleString()}</td>
                  <td className="text-center">{Number(r.passPersonAmount || 0).toLocaleString()}</td>
                  <td className="text-center">{fmtMoney(r.tutorAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.lunchAmountExpense)}</td>
                  <td className="text-center">{fmtMoney(r.utilityAmountExpense)}</td>
                  <td className="text-center">
                    {r.otherName ? `${r.otherName}: ` : ""}{fmtMoney(r.otherAmountExpense)}
                  </td>
                  <td className="text-center">{fmtMoney(total)}</td>
                  <td className="text-center">{toThaiDateLabel(r.paymentDate)}</td>
                  <td className="text-center">{fmtMoney(r.balanceTotalAmount)}</td>
                  <td className="text-center">
                    <button className="btn btn-sm btn-outline-primary" onClick={() => openEdit(r)}>แก้ไข</button>
                  </td>
                </tr>
              );
            })}
          </tbody>

          {items.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan={5} className="text-end fw-bold">รวม</td>
                <td className="text-center fw-bold">{fmtMoney(summary.tutor)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.lunch)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.util)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.other)}</td>
                <td className="text-center fw-bold">{fmtMoney(summary.total)}</td>
                <td />
                <td className="text-center fw-bold">{fmtMoney(summary.refund)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </Table>
      </div>

      {/* Modal */}
      <Modal isOpen={openModal} toggle={closeModal} centered size="lg" backdrop="static">
        <ModalHeader toggle={closeModal}>{editingItem ? "แก้ไขรายการ" : "เพิ่มรายการ"}</ModalHeader>
        <ModalBody>
          <ProjectForm
            deptId={deptId}
            reportMonth={reportMonth}
            editingItem={editingItem}
            onSaved={(saved) => {
              if (editingItem) {
                setItems(prev => prev.map(x => String(x.id) === String(saved.id) ? saved : x));
              } else {
                setItems(prev => [...prev, saved]);
              }
              closeModal();
            }}
          />
        </ModalBody>
      </Modal>

      {loading && (
        <div className="position-fixed bottom-0 end-0 p-3">
          <span className="badge bg-secondary">กำลังโหลด...</span>
        </div>
      )}
    </Container>
  );
}

// ===============================================================
//                          FORM
// ===============================================================
function ProjectForm({ deptId, reportMonth, editingItem, onSaved }) {
  const schema = Yup.object().shape({
    projectName: Yup.string().required("กรอกชื่อโครงการ"),
    courseName: Yup.string().required("กรอกหลักสูตร"),
    testingPersonAmount: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    passPersonAmount: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    tutorAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    lunchAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    utilityAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    otherName: Yup.string().nullable(),
    otherAmountExpense: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    balanceTotalAmount: Yup.number().typeError("ตัวเลข").min(0).required("กรอก"),
    paymentDate: Yup.string().required("เลือกวันที่จ่าย"),
  });

  const init = {
    id: editingItem?.id ?? editingItem?.supportOperationProjectId ?? null,
    deptId,
    projectName: editingItem?.projectName ?? "",
    courseName: editingItem?.courseName ?? "",
    testingPersonAmount: editingItem?.testingPersonAmount ?? 0,
    passPersonAmount: editingItem?.passPersonAmount ?? 0,
    tutorAmountExpense: editingItem?.tutorAmountExpense ?? 0,
    lunchAmountExpense: editingItem?.lunchAmountExpense ?? 0,
    utilityAmountExpense: editingItem?.utilityAmountExpense ?? 0,
    otherName: editingItem?.otherName ?? "",
    otherAmountExpense: editingItem?.otherAmountExpense ?? 0,
    paymentDate: editingItem?.paymentDate ?? "",
    balanceTotalAmount: editingItem?.balanceTotalAmount ?? 0,
  };

  const { y, m } = parseYearMonth(reportMonth || "2000-01");

  return (
    <Formik
      initialValues={init}
      validationSchema={schema}
      onSubmit={async (v, act) => {
        try {
          if (!sameMonth(v.paymentDate, y, m)) {
            Swal.fire("วันที่จ่ายต้องอยู่ในเดือนที่เลือก", "", "error");
            return;
          }
          const payload = {
            supportOperationProjectId: v.id,
            deptId: v.deptId,
            projectName: v.projectName,
            courseName: v.courseName,
            testingPersonAmount: Number(v.testingPersonAmount || 0),
            passPersonAmount: Number(v.passPersonAmount || 0),
            tutorAmountExpense: Number(v.tutorAmountExpense || 0),
            lunchAmountExpense: Number(v.lunchAmountExpense || 0),
            utilityAmountExpense: Number(v.utilityAmountExpense || 0),
            otherName: v.otherName || "",
            otherAmountExpense: Number(v.otherAmountExpense || 0),
            paymentDate: v.paymentDate,
            balanceTotalAmount: Number(v.balanceTotalAmount || 0),
          };

          const res = await fetch("/api/Support/saveProject", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const j = await res.json();

          if (j?.status === 1) {
            onSaved && onSaved({ ...v, id: v.id || j.id });
            Swal.fire("บันทึกสำเร็จ", "", "success");
          } else {
            Swal.fire("บันทึกไม่สำเร็จ", j?.message || "", "warning");
          }
        } catch (e) {
          Swal.fire("บันทึกไม่สำเร็จ", e?.message || "", "error");
        } finally {
          act.setSubmitting(false);
        }
      }}
    >
      {({ values, setFieldValue, touched, errors }) => {
        const selectedDate = values.paymentDate ? new Date(values.paymentDate) : null;
        const total =
          Number(values.tutorAmountExpense || 0) +
          Number(values.lunchAmountExpense || 0) +
          Number(values.utilityAmountExpense || 0) +
          Number(values.otherAmountExpense || 0);

        return (
          <Form>
            <Row className="g-3">
              <Col md={6}>
                <Label>โครงการ</Label>
                <Field name="projectName">
                  {({ field }) => (
                    <Input {...field} className={`rounded-pill ${touched.projectName && errors.projectName ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.projectName && errors.projectName && <div className="invalid-feedback d-block">{errors.projectName}</div>}
              </Col>

              <Col md={6}>
                <Label>หลักสูตร</Label>
                <Field name="courseName">
                  {({ field }) => (
                    <Input {...field} className={`rounded-pill ${touched.courseName && errors.courseName ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.courseName && errors.courseName && <div className="invalid-feedback d-block">{errors.courseName}</div>}
              </Col>

              <Col md={6}>
                <Label>จำนวนผู้เข้ารับการฝึกอบรมฯ (คน)</Label>
                <Field name="testingPersonAmount">
                  {({ field }) => (
                    <Input type="number" min="0" {...field}
                      className={`rounded-pill text-center ${touched.testingPersonAmount && errors.testingPersonAmount ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.testingPersonAmount && errors.testingPersonAmount && <div className="invalid-feedback d-block">{errors.testingPersonAmount}</div>}
              </Col>

              <Col md={6}>
                <Label>จำนวนผู้ผ่านการฝึกอบรมฯ (คน)</Label>
                <Field name="passPersonAmount">
                  {({ field }) => (
                    <Input type="number" min="0" {...field}
                      className={`rounded-pill text-center ${touched.passPersonAmount && errors.passPersonAmount ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.passPersonAmount && errors.passPersonAmount && <div className="invalid-feedback d-block">{errors.passPersonAmount}</div>}
              </Col>

              <Col md={6}>
                <Label>ค่าตอบแทนวิทยากร (บาท)</Label>
                <Field name="tutorAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.tutorAmountExpense && errors.tutorAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.tutorAmountExpense && errors.tutorAmountExpense && <div className="invalid-feedback d-block">{errors.tutorAmountExpense}</div>}
              </Col>

              <Col md={6}>
                <Label>ค่าอาหารกลางวัน (บาท)</Label>
                <Field name="lunchAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.lunchAmountExpense && errors.lunchAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.lunchAmountExpense && errors.lunchAmountExpense && <div className="invalid-feedback d-block">{errors.lunchAmountExpense}</div>}
              </Col>

              <Col md={6}>
                <Label>ค่าวัสดุ (บาท)</Label>
                <Field name="utilityAmountExpense">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.utilityAmountExpense && errors.utilityAmountExpense ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.utilityAmountExpense && errors.utilityAmountExpense && <div className="invalid-feedback d-block">{errors.utilityAmountExpense}</div>}
              </Col>

              <Col md={6}>
                <Label>ค่าใช้จ่ายอื่นๆ (ระบุ…)</Label>
                <Field name="otherName">
                  {({ field }) => (
                    <Input {...field} className="rounded-pill" placeholder="เช่น ค่าเช่าสถานที่" />
                  )}
                </Field>
                <div className="mt-2">
                  <Field name="otherAmountExpense">
                    {({ field }) => (
                      <Input type="number" min="0" step="0.01" {...field}
                        className={`rounded-pill text-center ${touched.otherAmountExpense && errors.otherAmountExpense ? "is-invalid" : ""}`}
                        placeholder="จำนวนเงิน (บาท)" />
                    )}
                  </Field>
                </div>
                {touched.otherAmountExpense && errors.otherAmountExpense && <div className="invalid-feedback d-block">{errors.otherAmountExpense}</div>}
              </Col>

              <Col md={6}>
                <Label>รวม (บาท)</Label>
                <Input readOnly className="rounded-pill text-center" value={fmtMoney(total)} />
              </Col>

              <Col md={6}>
                <Label>เงินคงเหลือส่งคืน (บาท)</Label>
                <Field name="balanceTotalAmount">
                  {({ field }) => (
                    <Input type="number" min="0" step="0.01" {...field}
                      className={`rounded-pill text-center ${touched.balanceTotalAmount && errors.balanceTotalAmount ? "is-invalid" : ""}`} />
                  )}
                </Field>
                {touched.balanceTotalAmount && errors.balanceTotalAmount && <div className="invalid-feedback d-block">{errors.balanceTotalAmount}</div>}
              </Col>

              <Col md={6}>
                <Label>วันที่จ่ายเงินช่วยเหลือ/อุดหนุน</Label>
                <DatePicker
                  selected={selectedDate}
                  locale="th-buddhist"
                  dateFormat="dd MMMM yyyy"
                  placeholderText="กรุณาเลือก"
                  renderCustomHeader={({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
                    <div className="d-flex justify-content-between align-items-center px-2 py-1" style={{ gap: 8 }}>
                      <button type="button" className="btn btn-sm btn-light" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>‹</button>
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <select className="form-select form-select-sm" style={{ width: 140 }}
                          value={thaiMonths[(selectedDate || date).getMonth()]}
                          onChange={({ target: { value } }) => changeMonth(thaiMonths.indexOf(value))}>
                          {thaiMonths.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select className="form-select form-select-sm" style={{ width: 110 }}
                          value={(selectedDate || date).getFullYear()}
                          onChange={({ target: { value } }) => changeYear(Number(value))}>
                          {years.map(y => <option key={y} value={y}>{y + 543}</option>)}
                        </select>
                      </div>
                      <button type="button" className="btn btn-sm btn-light" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>›</button>
                    </div>
                  )}
                  onChange={(date) => {
                    if (date instanceof Date && !isNaN(date)) {
                      const y2 = date.getFullYear();
                      const m2 = String(date.getMonth() + 1).padStart(2, "0");
                      const d2 = String(date.getDate()).padStart(2, "0");
                      const iso = `${y2}-${m2}-${d2}`;
                      if (!sameMonth(iso, y, m)) { Swal.fire("วันที่จ่ายต้องอยู่ในเดือนที่เลือก", "", "error"); return; }
                      setFieldValue("paymentDate", iso);
                    } else {
                      setFieldValue("paymentDate", "");
                    }
                  }}
                  customInput={
                    <Input type="text" readOnly
                      value={values.paymentDate ? toThaiDateLabel(values.paymentDate) : ""}
                      className={`rounded-pill text-center ${touched.paymentDate && errors.paymentDate ? "is-invalid" : ""}`} />
                  }
                />
                {touched.paymentDate && errors.paymentDate && <div className="invalid-feedback d-block">{errors.paymentDate}</div>}
              </Col>

              <div className="d-flex justify-content-between mt-3">
                <button type="button" className="btn btn-outline-secondary" onClick={() => window.history.back?.()}>ยกเลิก</button>
                <button type="submit" className="btn btn-success">{editingItem ? "บันทึกการแก้ไข" : "บันทึกรายการ"}</button>
              </div>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
}
